import * as React from 'react'
import { useChargebeeSubscriptionsWithoutAccounts, type ChargebeeSubscriptionWithoutAccount } from './api/get-chargebee-subscriptions-without-accounts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/DataTable'
import { ColumnDef, type PaginationState } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'

const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '—'
    try {
        return new Date(timestamp * 1000).toLocaleDateString()
    } catch {
        return '—'
    }
}

const columns: ColumnDef<ChargebeeSubscriptionWithoutAccount>[] = [
    {
        accessorKey: 'subscriptionId',
        header: 'Subscription ID',
        cell: ({ row }) => <span className='font-mono text-sm'>{row.getValue('subscriptionId')}</span>,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
            const status = row.getValue('status') as string
            const variant = status === 'active' ? 'default' : status === 'cancelled' ? 'destructive' : 'outline'
            return <Badge variant={variant}>{status}</Badge>
        },
    },
    {
        accessorKey: 'planId',
        header: 'Plan ID',
        cell: ({ row }) => <span>{row.getValue('planId')}</span>,
    },
    {
        accessorKey: 'billingPeriod',
        header: 'Billing Period',
        cell: ({ row }) => {
            const period = row.getValue('billingPeriod') as number | null
            const unit = row.original.billingPeriodUnit
            if (!period) return <span>—</span>
            return <span>{period} {unit || 'month(s)'}</span>
        },
    },
    {
        accessorKey: 'customerEmail',
        header: 'Customer Email',
        cell: ({ row }) => <span>{row.getValue('customerEmail')}</span>,
    },
    {
        accessorKey: 'customerCompany',
        header: 'Company',
        cell: ({ row }) => <span>{row.getValue('customerCompany') || '—'}</span>,
    },
    {
        accessorKey: 'currentTermStart',
        header: 'Term Start',
        cell: ({ row }) => <span>{formatDate(row.getValue('currentTermStart'))}</span>,
    },
    {
        accessorKey: 'currentTermEnd',
        header: 'Term End',
        cell: ({ row }) => <span>{formatDate(row.getValue('currentTermEnd'))}</span>,
    },
    {
        accessorKey: 'activatedAt',
        header: 'Activated',
        cell: ({ row }) => <span>{formatDate(row.getValue('activatedAt'))}</span>,
    },
]

export default function ChargebeeSubscriptionsWithoutAccountsReport() {
    const { data: subscriptionsWithoutAccounts = [], isLoading } = useChargebeeSubscriptionsWithoutAccounts({})
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 25,
    })

    // Manually paginate the data for client-side pagination
    const paginatedData = React.useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize
        const end = start + pagination.pageSize
        return subscriptionsWithoutAccounts.slice(start, end)
    }, [subscriptionsWithoutAccounts, pagination.pageIndex, pagination.pageSize])

    const handleExport = () => {
        if (!subscriptionsWithoutAccounts.length) return

        const csvRows = [
            ['Subscription ID', 'Status', 'Plan ID', 'Billing Period', 'Billing Unit', 'Customer Email', 'Company', 'Term Start', 'Term End', 'Activated'].join(','),
            ...subscriptionsWithoutAccounts.map(subscription => [
                `"${subscription.subscriptionId.replace(/"/g, '""')}"`,
                subscription.status,
                `"${subscription.planId.replace(/"/g, '""')}"`,
                subscription.billingPeriod ?? '',
                `"${(subscription.billingPeriodUnit || '').replace(/"/g, '""')}"`,
                `"${subscription.customerEmail.replace(/"/g, '""')}"`,
                `"${(subscription.customerCompany || '').replace(/"/g, '""')}"`,
                formatDate(subscription.currentTermStart),
                formatDate(subscription.currentTermEnd),
                formatDate(subscription.activatedAt),
            ].join(','))
        ]

        const csvContent = csvRows.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `chargebee_subscriptions_without_accounts_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle>Chargebee Subscriptions Without Accounts</CardTitle>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Chargebee subscriptions that do not exist in the SubscriptionV2 table
                            </p>
                        </div>
                        <Button onClick={handleExport} variant='outline' disabled={isLoading || subscriptionsWithoutAccounts.length === 0}>
                            <Download className='h-4 w-4 mr-2' />
                            Export CSV
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className='text-sm text-muted-foreground'>Loading subscriptions without accounts...</div>
                    ) : (
                        <>
                            {subscriptionsWithoutAccounts.length === 0 ? (
                                <div className='text-sm text-muted-foreground'>No Chargebee subscriptions without accounts found.</div>
                            ) : (
                                <>
                                    <div className='mb-4 text-sm text-muted-foreground'>
                                        Found {subscriptionsWithoutAccounts.length} Chargebee subscription{subscriptionsWithoutAccounts.length !== 1 ? 's' : ''} without accounts
                                    </div>
                                    <DataTable
                                        columns={columns}
                                        data={paginatedData}
                                        isLoading={isLoading}
                                        enablePagination={true}
                                        serverPagination={true}
                                        pageIndex={pagination.pageIndex}
                                        pageSize={pagination.pageSize}
                                        pageCount={Math.ceil(subscriptionsWithoutAccounts.length / pagination.pageSize)}
                                        onPaginationChange={setPagination}
                                        serverSorting={false}
                                        scrollContainerClassName='max-h-[calc(100vh-400px)] overflow-y-auto'
                                    />
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

